import React, { useEffect, useState, useMemo, useCallback, useReducer, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES, QUIZ_RUBRIC_PASS_POINTS } from "../../utils/constants";
import { getScenarioNumByPath, getTimer, shuffleQuizOptions } from "../../utils/helpers";
import { rubricPointsToScoreDelta } from "../../utils/quizScoring";
import { getRandomEstasseguroAvatar } from "../../utils/estasseguroAvatar";
import { setGroup, updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
	modalConfirm,
	modalQuestionResolution,
	modalHint,
	modalHintShow,
} from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import { getRandomAciertoAvatar, getRandomErrorAvatar } from "../../utils/quizResultAvatars";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import HintMarkPista, { hintMarkPistaHtml } from "../../components/HintMarkPista";
import imageHintUsed from "../../assets/btnPistaViolet.png";
import unIntento from "../../assets/un-intento.png";
import dosIntentos from "../../assets/dos-intentos.png";
import "../StartGame.css";
import "../Scenario2/Scenario2.css";
import "./Scenario3.css";

const SC3_SWAL_POPUP_CLASS = "modal-sg5 modal-sg5--fepol";
const SC3_HINT_THEME_CLASS = `${SC3_SWAL_POPUP_CLASS} modal-sg5--hint-sc2`.trim();
const SC3_HINT_MARK_HTML = hintMarkPistaHtml();

const SC3_QUIZ2_HINT_COST_POINTS = 20;

const QUESTION_STEM =
	"Tras escuchar ambas declaraciones, clasifica cada elemento según corresponda.";

const RETROALIMENTACION =
	"Coincidencias: ambos en la plaza, cartera no en el bolso, discusión tras echarla en falta, la mujer no vio la sustracción directa. Contradicciones o pendientes: creencia de implicación del hombre, negación del hombre, versión de la espera del amigo, actitud defensiva según la mujer, y lo no acreditado (si alguien vio la sustracción, dónde está la cartera).";

const HINT_HTML = `<div class="txtTexto1 mb-2">Las <b>coincidencias</b> suelen ser puntos en los que ambas versiones o los datos objetivos encajan. Las <b>contradicciones o pendientes</b> señalan discrepancias o lagunas que aún habría que contrastar.</div>`;

const ITEMS = [
	{ id: "ambos-plaza", text: "Ambos estaban en la plaza.", category: "coincidencia" },
	{
		id: "no-vio-directo",
		text: "La mujer no vio directamente cómo el hombre cogía la cartera.",
		category: "coincidencia",
	},
	{ id: "cartera-bolso", text: "La cartera no aparece en el bolso de la mujer.", category: "coincidencia" },
	{
		id: "discusion-despues",
		text: "La discusión comenzó después de que la mujer echara en falta la cartera.",
		category: "coincidencia",
	},
	{
		id: "mujer-cree-relacion",
		text: "La mujer cree que el hombre está relacionado con la desaparición.",
		category: "contradiccion",
	},
	{ id: "hombre-niega", text: "El hombre niega haber cogido nada.", category: "contradiccion" },
	{ id: "hombre-amigo", text: "El hombre dice que estaba esperando a un amigo.", category: "contradiccion" },
	{
		id: "mujer-defensiva",
		text: "La mujer afirma que el hombre se puso a la defensiva.",
		category: "contradiccion",
	},
	{
		id: "no-sabe-sustraccion",
		text: "No se sabe si alguien vio realmente la sustracción.",
		category: "contradiccion",
	},
	{ id: "no-sabe-cartera", text: "No se sabe dónde está la cartera.", category: "contradiccion" },
];

const COINCIDENCIA_IDS = new Set(ITEMS.filter((i) => i.category === "coincidencia").map((i) => i.id));
const CONTRADICCION_IDS = new Set(ITEMS.filter((i) => i.category === "contradiccion").map((i) => i.id));

const ACTIVITY_TEXT = Object.fromEntries(ITEMS.map((i) => [i.id, i.text]));

const BUCKET_KEYS = ["pool", "coincidencias", "contradicciones"];

const WEIGHTED_MAX_POINTS = 100;
const WEIGHTED_MAX_GAME_DELTA = 10;
const NUMBER_ATTEMPTS = 2;

function initialPoolIds() {
	const shuffled = shuffleQuizOptions(ITEMS.map((r) => ({ id: r.id, text: r.text })));
	return shuffled.map((o) => o.id);
}

function initialBoard() {
	return {
		pool: initialPoolIds(),
		coincidencias: [],
		contradicciones: [],
	};
}

function isClassificationCorrect(state) {
	if (state.pool.length !== 0) return false;
	if (state.coincidencias.length !== COINCIDENCIA_IDS.size || state.contradicciones.length !== CONTRADICCION_IDS.size) {
		return false;
	}
	for (const id of state.coincidencias) {
		if (!COINCIDENCIA_IDS.has(id)) return false;
	}
	for (const id of state.contradicciones) {
		if (!CONTRADICCION_IDS.has(id)) return false;
	}
	return true;
}

function boardReducer(state, action) {
	switch (action.type) {
		case "RESET":
			return initialBoard();
		case "MOVE": {
			const { activityId, from, to } = action;
			if (from === to) return state;
			if (!BUCKET_KEYS.includes(from) || !BUCKET_KEYS.includes(to)) return state;
			const fromArr = state[from];
			if (!fromArr.includes(activityId)) return state;
			return {
				...state,
				[from]: fromArr.filter((id) => id !== activityId),
				[to]: [...state[to], activityId],
			};
		}
		default:
			return state;
	}
}

function Scenario3Quiz2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.SCENARIO3_VERSIONS_COMPARE_QUIZ;
	const SCENARIO_ID = SCENARIOS.SCENARIO_3;

	const [board, dispatchBoard] = useReducer(boardReducer, undefined, initialBoard);
	const { pool, coincidencias, contradicciones } = board;

	const [attempt, setAttempt] = useState(1);
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [seededBaseScore, setSeededBaseScore] = useState(false);
	const capitalAtQuizStartRef = useRef(null);

	const dndLocked = isCompleted || isSubmitting;

	useEffect(() => {
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		if (capitalAtQuizStartRef.current === null) capitalAtQuizStartRef.current = s;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(RETROALIMENTACION, "violet");

	const allPlaced = useMemo(
		() => pool.length === 0 && coincidencias.length + contradicciones.length === ITEMS.length,
		[pool.length, coincidencias.length, contradicciones.length]
	);

	const dragSessionRef = useRef(null);
	const ghostElRef = useRef(null);
	const [dragGhost, setDragGhost] = useState(null);

	const positionGhostFromSession = useCallback(() => {
		const el = ghostElRef.current;
		const d = dragSessionRef.current;
		if (!el || !d?.ghostShown) return;
		const maxW = typeof window !== "undefined" ? Math.min(d.width, window.innerWidth * 0.92) : d.width;
		el.style.width = `${maxW}px`;
		el.style.minHeight = `${Math.max(d.height, 28)}px`;
		el.style.left = `${d.lastClientX - d.offX}px`;
		el.style.top = `${d.lastClientY - d.offY}px`;
	}, []);

	const endPointerDrag = useCallback((el, pointerId) => {
		try {
			if (el?.hasPointerCapture?.(pointerId)) el.releasePointerCapture(pointerId);
		} catch {
			/* ignore */
		}
		dragSessionRef.current = null;
		setDragGhost(null);
		document.body.classList.remove("sc3-chrono-dnd-active");
	}, []);

	const applyDropFromPayload = useCallback((payload, target) => {
		if (!target?.kind) return;
		const to = target.kind;
		const from = payload.source;
		const activityId = payload.activityId;
		if (from === to) return;
		dispatchBoard({ type: "MOVE", activityId, from, to });
	}, []);

	const resolveDropTarget = useCallback((clientX, clientY) => {
		const stack = document.elementsFromPoint(clientX, clientY);
		for (const node of stack) {
			if (!(node instanceof Element)) continue;
			if (node.closest(".sc3-chrono-drag-ghost")) continue;
			const zone = node.closest("[data-sc3q2-drop]");
			if (zone) {
				const v = zone.getAttribute("data-sc3q2-drop");
				if (v === "pool" || v === "coincidencias" || v === "contradicciones") return { kind: v };
			}
		}
		return null;
	}, []);

	const handleChipPointerDown = useCallback(
		(e, payload) => {
			if (dndLocked) return;
			if (e.pointerType === "mouse" && e.button !== 0) return;
			const rect = e.currentTarget.getBoundingClientRect();
			dragSessionRef.current = {
				payload,
				startX: e.clientX,
				startY: e.clientY,
				pointerId: e.pointerId,
				moved: false,
				ghostShown: false,
				offX: e.clientX - rect.left,
				offY: e.clientY - rect.top,
				width: rect.width,
				height: rect.height,
				lastClientX: e.clientX,
				lastClientY: e.clientY,
			};
			e.currentTarget.setPointerCapture(e.pointerId);
			e.preventDefault();
		},
		[dndLocked]
	);

	const handleChipPointerMove = useCallback(
		(e) => {
			const d = dragSessionRef.current;
			if (!d || d.pointerId !== e.pointerId) return;
			d.lastClientX = e.clientX;
			d.lastClientY = e.clientY;
			const dx = e.clientX - d.startX;
			const dy = e.clientY - d.startY;
			if (dx * dx + dy * dy > 36) {
				d.moved = true;
				document.body.classList.add("sc3-chrono-dnd-active");
				if (!d.ghostShown) {
					d.ghostShown = true;
					const aid = d.payload.activityId;
					setDragGhost({
						activityId: aid,
						label: ACTIVITY_TEXT[aid] ?? aid,
						width: d.width,
						height: d.height,
					});
				}
			}
			if (d.ghostShown && ghostElRef.current) positionGhostFromSession();
		},
		[positionGhostFromSession]
	);

	useLayoutEffect(() => {
		if (!dragGhost) return;
		positionGhostFromSession();
	}, [dragGhost, positionGhostFromSession]);

	const handleChipPointerUpOrCancel = useCallback(
		(e) => {
			const d = dragSessionRef.current;
			if (!d || d.pointerId !== e.pointerId) return;
			const el = e.currentTarget;
			const { payload, moved } = d;

			if (moved) {
				const target = resolveDropTarget(e.clientX, e.clientY);
				if (target) applyDropFromPayload(payload, target);
			}

			endPointerDrag(el, e.pointerId);
		},
		[applyDropFromPayload, endPointerDrag, resolveDropTarget]
	);

	useEffect(() => {
		return () => {
			document.body.classList.remove("sc3-chrono-dnd-active");
		};
	}, []);

	async function submitAnswer(finalScore, correct, extra = {}) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const base = capitalAtQuizStartRef.current;
			const capitalInicio = Number.isFinite(base) ? base : 0;
			const deltaParaApi = finalScore - capitalInicio;

			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: deltaParaApi,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO3_CONTEXT3,
				correct,
				...extra,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch(setGroup(serverGroup));
				}

				setIsCompleted(true);

				navigate(ROUTES.SCENARIO3_CONTEXT3);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					themePopupClass: SC3_SWAL_POPUP_CLASS,
				});

				navigate(ROUTES.SCENARIO3_CONTEXT3);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC3_SWAL_POPUP_CLASS,
			});

			setIsSubmitting(false);
		}
	}

	const useHint = async () => {
		if (hintUsed) {
			await modalHintShow({
				hint: HINT_HTML,
				hintMarkHtml: SC3_HINT_MARK_HTML,
				color: "violet",
				big: false,
				themePopupClass: SC3_HINT_THEME_CLASS,
			});
			return;
		}

		const { used } = await modalHint({
			image: getRandomEstasseguroAvatar(),
			imageHintUsed,
			hint: HINT_HTML,
			hintMarkHtml: SC3_HINT_MARK_HTML,
			color: "violet",
			big: false,
			themePopupClass: SC3_HINT_THEME_CLASS,
		});
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(SC3_QUIZ2_HINT_COST_POINTS);
		}
	};

	function finishQuestion(rubricPointsAwarded) {
		const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
		const scoreFromRubric = rubricPointsToScoreDelta(
			rubricPointsAwarded,
			WEIGHTED_MAX_POINTS,
			WEIGHTED_MAX_GAME_DELTA
		);
		const attemptDelta = scoreFromRubric - hintThisAttempt;
		const newScore = pendingScore + attemptDelta;
		if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

		const capitalInicio = capitalAtQuizStartRef.current ?? 0;
		const netoEnEstaPregunta = newScore - capitalInicio;
		const rubricPass = rubricPointsAwarded >= QUIZ_RUBRIC_PASS_POINTS;
		const avatarUrl = rubricPass ? getRandomAciertoAvatar() : getRandomErrorAvatar();
		const outcomeStyle = rubricPass ? "correct" : "incorrect";

		modalQuestionResolution({
			correct: rubricPass,
			question: "Coincidencias y contradicciones",
			textHtml: `${buildQuizResultAvatarHtml(avatarUrl, outcomeStyle, "violet")}
              <div class="txt28 bold pt-3">${rubricPointsAwarded} puntos</div>
              <div class="txt22 mt-2">${rubricPass ? "Has acertado." : "No has acertado."}</div>
              <div class="txt20 mt-2">Puntos en el marcador (esta pregunta): <b>${netoEnEstaPregunta >= 0 ? "+" : ""}${netoEnEstaPregunta}</b> pt${
					hintThisAttempt ? ` (incluye −${hintThisAttempt} pt por pista)` : ""
				}</div>
              ${retroalimentacionBlockHtml}`,
			customConfirmButtonHtml: "Continuar",
			color: "violet",
			themePopupClass: SC3_SWAL_POPUP_CLASS,
		}).then(() => {
			submitAnswer(newScore, rubricPass, { rubricPoints: rubricPointsAwarded });
		});
	}

	function renderDraggableChip(activityId, source) {
		return (
			<div
				key={`${source}-${activityId}`}
				tabIndex={0}
				className={`sc3-chrono-chip sc3-quiz1-classify-chip ${dragGhost?.activityId === activityId ? "sc3-chrono-chip--drag-source" : ""}`}
				onPointerDown={(e) => handleChipPointerDown(e, { source, activityId })}
				onPointerMove={handleChipPointerMove}
				onPointerUp={handleChipPointerUpOrCancel}
				onPointerCancel={handleChipPointerUpOrCancel}
				onLostPointerCapture={(e) => {
					if (dragSessionRef.current?.pointerId === e.pointerId) {
						endPointerDrag(e.currentTarget, e.pointerId);
					}
				}}
				onContextMenu={(ev) => ev.preventDefault()}
				title={ACTIVITY_TEXT[activityId]}
			>
				{ACTIVITY_TEXT[activityId]}
			</div>
		);
	}

	function checkAnswer() {
		if (!allPlaced) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${getRandomEstasseguroAvatar()}" alt="" class="estasseguro swal-estasseguro-img" /></div></div><div class="text-center txtTitulos mt-3 violet">Clasifica los diez elementos en los dos grupos (no puede quedar ninguno sin colocar).</div>`,
				themePopupClass: SC3_SWAL_POPUP_CLASS,
			});
			return;
		}

		modalConfirm({
			estasseguro: getRandomEstasseguroAvatar(),
			color: "violet",
			themePopupClass: SC3_SWAL_POPUP_CLASS,
		}).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			const correct = isClassificationCorrect(board);

			if (correct) {
				const rubricPointsAwarded = attempt === 1 ? 100 : 50;
				finishQuestion(rubricPointsAwarded);
				return;
			}

			if (attempt < NUMBER_ATTEMPTS) {
				modalQuestionResolution({
					correct: false,
					question: "Coincidencias y contradicciones",
					textHtml: `${buildQuizResultAvatarHtml(getRandomErrorAvatar(), "incorrect", "violet")}
                    <div class="txt28 bold pt-3">0 puntos</div>
                    <div class="txt22 mt-2">No has acertado. Tienes <b>un segundo intento</b>: revisa las categorías y vuelve a comprobar.</div>`,
					customConfirmButtonHtml: "Continuar",
					color: "violet",
					themePopupClass: SC3_SWAL_POPUP_CLASS,
				}).then(() => {
					dispatchBoard({ type: "RESET" });
					setAttempt(2);
					setIsSubmitting(false);
				});
				return;
			}

			finishQuestion(0);
		});
	}

	const remainingAttempts = NUMBER_ATTEMPTS - attempt + 1;

	return (
		<div className="start-game-page start-game-page--welcome sc2-context-page sc2-quiz-page sc2-quiz1-brand sc3-chrono-quiz sc3-quiz1-classify sc3-quiz2-classify">
			<div className="start-game-inner start-game-inner--prep5">
				<div className="sg5-scroll">
					<header className="sg5-hero">
						<div className="sc3-chrono-hero-head">
							<h1 className="sg5-title">Pregunta</h1>
							<span className="start-game-badge sg5-badge sc3-chrono-hero-badge">
								<span className="start-game-badge-dot" aria-hidden />
								Escenario 3 · Evolución
							</span>
						</div>
						<p className="sg5-lead sc2-quiz-stem sc3-chrono-stem sc3-quiz1-classify-stem">{QUESTION_STEM}</p>
					</header>

					<div className="sc2-quiz-toolbar sc3-chrono-toolbar">
						<p className="sc2-quiz-hint-line mb-0">
							<IconHandClick
								size={28}
								color="rgba(239, 239, 237, 0.85)"
								stroke={1.25}
								className="sc2-quiz-hand me-2"
							/>
							<span>
								Arrastra cada elemento al grupo que corresponda. El orden dentro de cada grupo no importa.
								Dos intentos (100 / 50 puntos). La pista resta {SC3_QUIZ2_HINT_COST_POINTS} pt al marcador.
							</span>
						</p>
						<img
							src={remainingAttempts === 2 ? dosIntentos : unIntento}
							alt=""
							className="sc2-quiz-intentos"
						/>
					</div>

					<div className="sc3-quiz1-buckets sc3-quiz2-buckets" aria-label="Clasificación en dos grupos">
						<div
							className="sc3-quiz1-bucket sc3-quiz1-bucket--facts sc3-quiz2-bucket"
							data-sc3q2-drop="coincidencias"
							aria-label="Coincidencias entre versiones"
						>
							<h2 className="sc3-quiz1-bucket-title">Coincidencias entre versiones</h2>
							<p className="sc3-quiz1-bucket-hint">Puntos en común o datos objetivamente alineados.</p>
							<div className="sc3-quiz1-bucket-chips">
								{coincidencias.length === 0 ? (
									<span className="sc3-quiz1-bucket-empty">Soltar aquí</span>
								) : (
									coincidencias.map((id) => renderDraggableChip(id, "coincidencias"))
								)}
							</div>
						</div>

						<div
							className="sc3-quiz1-bucket sc3-quiz1-bucket--valuations sc3-quiz2-bucket"
							data-sc3q2-drop="contradicciones"
							aria-label="Contradicciones o puntos pendientes de comprobar"
						>
							<h2 className="sc3-quiz1-bucket-title">Contradicciones o puntos pendientes de comprobar</h2>
							<p className="sc3-quiz1-bucket-hint">Divergencias o lagunas que requieren comprobación.</p>
							<div className="sc3-quiz1-bucket-chips">
								{contradicciones.length === 0 ? (
									<span className="sc3-quiz1-bucket-empty">Soltar aquí</span>
								) : (
									contradicciones.map((id) => renderDraggableChip(id, "contradicciones"))
								)}
							</div>
						</div>
					</div>

					<div className="sc3-chrono-pool-wrap sc3-quiz1-pool-wrap">
						<p className="sc3-chrono-pool-label mb-2">Elementos (arrastra desde aquí)</p>
						<div
							className="sc3-chrono-pool sc3-quiz1-pool"
							data-sc3q2-drop="pool"
							aria-label="Elementos sin clasificar"
						>
							{pool.length === 0 ? (
								<span className="sc3-chrono-pool-empty">Todos los elementos están clasificados</span>
							) : (
								pool.map((activityId) => renderDraggableChip(activityId, "pool"))
							)}
						</div>
					</div>
				</div>

				<div className="sg5-actions sc2-quiz-footer">
					<div className="sc2-quiz-hint-wrap position-relative d-inline-block">
						<button
							type="button"
							className={`sc2-hint-trigger sc2-quiz-hint-btn ${hintUsed ? "used" : ""}`}
							onClick={useHint}
							aria-label={hintUsed ? "Pista usada" : "Usar pista"}
							title={
								hintUsed
									? `Pista usada (−${SC3_QUIZ2_HINT_COST_POINTS} pt)`
									: `Usar pista (−${SC3_QUIZ2_HINT_COST_POINTS} pt)`
							}
						>
							<HintMarkPista />
						</button>
						{hintUsed ? (
							<span className="badge-penalty sc2-quiz-hint-badge">−{SC3_QUIZ2_HINT_COST_POINTS} pt</span>
						) : null}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="orange"
						loading={isSubmitting}
						bare
						className="sc2-quiz-check-wrap"
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />

			{typeof document !== "undefined" &&
				dragGhost &&
				createPortal(
					<div
						ref={ghostElRef}
						className="sc3-chrono-drag-ghost"
						role="presentation"
						aria-hidden="true"
					>
						{dragGhost.label}
					</div>,
					document.body
				)}
		</div>
	);
}

export default Scenario3Quiz2;
